/**
 * Checks if a URL starts with http:// or https://
 */
export function hasHttpProtocol(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Ensures a URL has an http/https protocol, adding https:// if missing
 */
export function ensureHttpProtocol(url: string): string {
  return hasHttpProtocol(url) ? url : `https://${url}`;
}

function getUrlToOpen(link: string): string {
  if (hasHttpProtocol(link)) {
    return link;
  } else if (link.startsWith('/')) {
    // URL starts with /, it is a relative path
    return link;
  } else {
    return `https://${link}`;
  }
}

/**
 * True when the browser can parse the URL, i.e. `window.open` won't reject it.
 *
 * Resolved against the document the same way `window.open` resolves its
 * argument, so the relative paths `getUrlToOpen` passes through still count.
 */
function canOpenUrl(url: string): boolean {
  try {
    new URL(url, window.location.href);
    return true;
  } catch {
    return false;
  }
}

export function openLink(url: string) {
  const urlToOpen = getUrlToOpen(url);

  // A link comes from cell content, so it isn't necessarily a URL at all, and
  // `window.open` throws a SyntaxError on anything it can't parse. An imported
  // xlsx keeps Excel's internal links verbatim — a jump to another sheet is
  // stored as `#Sheet_Name!A1` — and `getUrlToOpen` prefixes that with
  // `https://`, leaving no host (`https://#Sheet_Name!A1`). That threw out of
  // the hyperlink popup's Open button and out of cmd/ctrl-click on the grid
  // (INTERNAL-15B and INTERNAL-6PV are the same crash, both on
  // `#Proforma_Baslik!A1` in an imported sheet — the latter reported from a
  // 0.26.11 bundle built before this guard landed); the same is true of an
  // empty or fragment-only link.
  //
  // Nothing sensible can open in a tab for those, so skip the call: a dead
  // click beats throwing out of a pointer/click handler. Following Excel's
  // internal links to the referenced sheet would be a feature on top of this,
  // not part of not-crashing.
  if (!canOpenUrl(urlToOpen)) {
    return;
  }

  window.open(urlToOpen, '_blank', 'noopener,noreferrer');
}
